import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import {
  flashSpyRemainingMs,
  formatFlashSpyCountdown,
  shouldShowFlashSpyUpsell,
} from './flash-spy-timer.js';
import './FlashSpyOverlay.css';

export interface FlashSpyOverlayProps {
  expiresAt: string;
  isSubmitting: boolean;
  onJoinFull: () => void;
  onContinueUnlimited: () => void;
  onExpired: () => void;
}

export function FlashSpyOverlay({
  expiresAt,
  isSubmitting,
  onJoinFull,
  onContinueUnlimited,
  onExpired,
}: FlashSpyOverlayProps): ReactElement | null {
  const [remainingMs, setRemainingMs] = useState(() => flashSpyRemainingMs(expiresAt));
  const expiredRef = useRef(false);

  useEffect(() => {
    expiredRef.current = false;
    const tick = (): void => {
      const next = flashSpyRemainingMs(expiresAt);
      setRemainingMs(next);
      if (next <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpired();
      }
    };
    tick();
    const intervalId = window.setInterval(tick, 250);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [expiresAt, onExpired]);

  if (remainingMs <= 0) {
    return null;
  }

  const showUpsell = shouldShowFlashSpyUpsell(remainingMs);

  return (
    <div className="flash-spy-overlay" role="status" aria-live="polite">
      <div className="flash-spy-overlay__timer">
        {t('common.show.flashSpyTimer').replace('{{time}}', formatFlashSpyCountdown(remainingMs))}
      </div>
      {showUpsell ? (
        <div className="flash-spy-overlay__card">
          <p className="flash-spy-overlay__title">{t('common.show.flashSpyUpsellTitle')}</p>
          <p className="flash-spy-overlay__hint">{t('common.show.flashSpyUpsellBody')}</p>
          <div className="flash-spy-overlay__actions">
            <Button
              label={t('common.show.flashSpyJoinFull')}
              disabled={isSubmitting}
              onClick={onJoinFull}
            />
            <button
              type="button"
              className="flash-spy-overlay__secondary"
              disabled={isSubmitting}
              onClick={onContinueUnlimited}
            >
              {t('common.show.flashSpyContinue')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
